import {
  DollarSign,
  Edit,
  Eye,
  History,
  Loader2,
  Mail,
  Phone,
  Plus,
  ShoppingBag,
  Trash2,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ClientDetailModal } from '../components/clients/modals/ClientDetailModal'
import { ClientRedemptionsModal } from '../components/clients/modals/ClientRedemptionsModal'
import {
  EditClientModal,
  NewClientModal,
} from '../components/clients/modals/NewClientModal'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Button } from '../components/ui/button'
import { GlobalPaginator } from '../components/ui/global-paginator'
import {
  ClientItem,
  ClientListResult,
  ClientSummary,
  clientsService,
} from '../services/clients.service'
import { useSessionStore } from '../store/auth/auth.store'

const emptyList: ClientListResult = {
  data: [],
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
}

const emptySummary: ClientSummary = {
  customersCount: 0,
  purchasesCount: 0,
  netPurchasesTotal: 0,
}

const money = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
})

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(
    new Date(value),
  )
}

export function Clients() {
  const canCreateCustomer = useSessionStore((state) => state.canAction('clients.customer.create'))
  const canViewCustomerDetail = useSessionStore((state) => state.canAction('clients.customer.detail'))
  const canViewRedemptions = useSessionStore((state) => state.canAction('loyalty.redemption.view'))
  const canEditCustomer = useSessionStore((state) => state.canAction('clients.customer.edit'))
  const canDeleteCustomer = useSessionStore((state) => state.canAction('clients.customer.delete'))
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [list, setList] = useState<ClientListResult>(emptyList)
  const [summary, setSummary] = useState<ClientSummary>(emptySummary)
  const [selectedClient, setSelectedClient] = useState<ClientItem | null>(null)
  const [editingClient, setEditingClient] = useState<ClientItem | null>(null)
  const [redemptionsClient, setRedemptionsClient] = useState<ClientItem | null>(null)
  const [deleteClient, setDeleteClient] = useState<ClientItem | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showNewClientModal, setShowNewClientModal] = useState(false)
  const [deleteImpactLoadingId, setDeleteImpactLoadingId] = useState<
    number | null
  >(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const loadClients = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)
    try {
      const data = await clientsService.getItems({
        page,
        per_page: 15,
        search: searchTerm || undefined,
        date_from: startDate || undefined,
        date_to: endDate || undefined,
      })
      setList(data)
    } catch {
      setHasError(true)
      toast.error('No se pudo cargar la lista de clientes')
    } finally {
      setIsLoading(false)
    }
  }, [endDate, page, searchTerm, startDate])

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await clientsService.getSummary())
    } catch {
      toast.error('No se pudo cargar el resumen de clientes')
    }
  }, [])

  useEffect(() => {
    void loadClients()
  }, [loadClients])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  const openDetailModal = (client: ClientItem) => {
    if (!canViewCustomerDetail) return

    setSelectedClient(client)
    setShowDetailModal(true)
  }

  const openDeleteModal = async (client: ClientItem) => {
    if (!canDeleteCustomer || !canViewCustomerDetail) return

    setDeleteImpactLoadingId(client.id)
    try {
      setDeleteClient(await clientsService.getItem(client.id))
    } catch {
      toast.error('No se pudo obtener el impacto de eliminar al cliente')
    } finally {
      setDeleteImpactLoadingId(null)
    }
  }

  const executeDelete = async () => {
    if (!deleteClient || !canDeleteCustomer) return

    setIsDeleting(true)
    try {
      await clientsService.deleteItem(deleteClient.id)
      toast.success('Cliente eliminado exitosamente')

      if (selectedClient?.id === deleteClient.id) {
        setSelectedClient(null)
        setShowDetailModal(false)
      }
      if (editingClient?.id === deleteClient.id) setEditingClient(null)
      if (redemptionsClient?.id === deleteClient.id) setRedemptionsClient(null)

      setDeleteClient(null)
      await loadSummary()

      if (list.data.length === 1 && page > 1) {
        setPage((current) => current - 1)
      } else {
        await loadClients()
      }
    } catch (caught: unknown) {
      const response = caught as { response?: { data?: { message?: string } } }
      toast.error(
        response.response?.data?.message || 'No se pudo eliminar el cliente',
      )
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Clientes (CRM)
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {summary.customersCount} clientes registrados
          </p>
        </div>
        {canCreateCustomer && (
          <Button onClick={() => setShowNewClientModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          icon={Users}
          label="Total Clientes"
          value={String(summary.customersCount)}
          tone="primary"
        />
        <SummaryCard
          icon={DollarSign}
          label="Compras Totales"
          value={money.format(summary.netPurchasesTotal)}
          tone="green"
        />
        <SummaryCard
          icon={ShoppingBag}
          label="Número de Compras"
          value={String(summary.purchasesCount)}
          tone="blue"
        />
      </div>

      <GlobalFilterBar
        show={['search', 'dates']}
        values={{ search: searchTerm, startDate, endDate }}
        onChange={(changes) => {
          if (changes.search !== undefined) setSearchTerm(changes.search.trim())
          if (changes.startDate !== undefined) setStartDate(changes.startDate)
          if (changes.endDate !== undefined) setEndDate(changes.endDate)
          setPage(1)
        }}
        onClearFilters={() => {
          setSearchTerm('')
          setStartDate('')
          setEndDate('')
          setPage(1)
        }}
        hasActiveFilters={Boolean(searchTerm || startDate || endDate)}
        customOptions={{
          searchPlaceholder: 'Buscar cliente por nombre, email o teléfono...',
        }}
      />

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                {[
                  'Cliente',
                  'Contacto',
                  'Compras Totales',
                  'Puntos',
                  'Última Compra',
                  'Acciones',
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-sm text-slate-500"
                  >
                    Cargando clientes...
                  </td>
                </tr>
              ) : hasError ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Button
                      variant="outline"
                      onClick={() => void loadClients()}
                    >
                      Reintentar
                    </Button>
                  </td>
                </tr>
              ) : list.data.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-sm text-slate-500"
                  >
                    No se encontraron clientes.
                  </td>
                </tr>
              ) : (
                list.data.map((client) => (
                  <tr key={client.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <ClientAvatar client={client} />
                        <span className="font-medium text-slate-900">
                          {client.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-sm text-slate-600">
                        {client.emails[0] && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            {client.emails[0]}
                          </div>
                        )}
                        {client.phones[0] && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {client.phones[0]}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-green-600">
                      {money.format(client.purchaseMetrics.netPurchasesTotal)}
                    </td>
                    <td className="px-6 py-4 font-medium text-purple-700">
                      {client.loyalty?.currentTier.medal || '🥉'}{' '}
                      {(client.loyalty?.pointsBalance ?? 0).toLocaleString('es-MX')}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {formatDate(client.purchaseMetrics.lastPurchaseAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {canViewRedemptions && (
                          <button
                            type="button"
                            aria-label={`Historial de canjes de ${client.name}`}
                            title="Historial de canjes"
                            onClick={() => setRedemptionsClient(client)}
                            className="rounded p-2 hover:bg-purple-50"
                          >
                            <History className="h-4 w-4 text-purple-700" />
                          </button>
                        )}
                        {canViewCustomerDetail && (
                          <button
                            type="button"
                            aria-label={`Ver perfil de ${client.name}`}
                            title="Ver perfil"
                            onClick={() => openDetailModal(client)}
                            className="rounded p-2 hover:bg-slate-100"
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </button>
                        )}
                        {canEditCustomer && canViewCustomerDetail && (
                          <button
                            type="button"
                            aria-label={`Editar cliente ${client.name}`}
                            title="Editar"
                            onClick={() => setEditingClient(client)}
                            className="rounded p-2 hover:bg-slate-100"
                          >
                            <Edit className="h-4 w-4 text-slate-600" />
                          </button>
                        )}
                        {canDeleteCustomer && canViewCustomerDetail && (
                          <button
                            type="button"
                            aria-label={`Eliminar cliente ${client.name}`}
                            title="Eliminar"
                            disabled={deleteImpactLoadingId !== null}
                            onClick={() => void openDeleteModal(client)}
                            className="rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deleteImpactLoadingId === client.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <GlobalPaginator
          totalItems={list.total}
          itemsPerPage={list.perPage}
          currentPage={list.currentPage}
          from={list.from}
          to={list.to}
          onPageChange={setPage}
        />
      </div>

      {canViewCustomerDetail && (
        <ClientDetailModal
          client={selectedClient}
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
        />
      )}
      {canViewRedemptions && (
        <ClientRedemptionsModal
          client={redemptionsClient}
          open={redemptionsClient !== null}
          onOpenChange={(isOpen) => !isOpen && setRedemptionsClient(null)}
        />
      )}
      {canCreateCustomer && (
        <NewClientModal
          open={showNewClientModal}
          onOpenChange={setShowNewClientModal}
          onSuccess={() => {
            setPage(1)
            void loadClients()
            void loadSummary()
          }}
        />
      )}
      {canEditCustomer && canViewCustomerDetail && (
        <EditClientModal
          client={editingClient}
          open={editingClient !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setEditingClient(null)
          }}
          onSuccess={(updated) => {
            setEditingClient(null)
            if (selectedClient?.id === updated.id) setSelectedClient(updated)
            void loadClients()
            void loadSummary()
          }}
        />
      )}
      <ConfirmationModal
        isOpen={deleteClient !== null}
        onClose={() => {
          if (!isDeleting) setDeleteClient(null)
        }}
        onConfirm={executeDelete}
        preset="delete"
        itemName="cliente"
        confirmText="Sí, eliminar cliente"
        isLoading={isDeleting}
        loadingText="Eliminando..."
        description={
          deleteClient
            ? `¿Deseas eliminar a ${deleteClient.name}? Esta acción es irreversible.`
            : undefined
        }
        details={
          deleteClient ? (
            <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
              <p className="font-medium text-red-900">
                Se eliminarán el perfil, sus contactos, dirección, avatar e
                historial CRM.
              </p>
              <div className="grid grid-cols-2 gap-3 text-red-800">
                <div>
                  <span className="block text-xs text-red-600">
                    Compras a descontar
                  </span>
                  <strong>{deleteClient.purchaseMetrics.purchasesCount}</strong>
                </div>
                <div>
                  <span className="block text-xs text-red-600">
                    Total neto a descontar
                  </span>
                  <strong>
                    {money.format(
                      deleteClient.purchaseMetrics.netPurchasesTotal,
                    )}
                  </strong>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Los tickets, pagos, devoluciones y registros contables se
                conservarán.
              </p>
            </div>
          ) : undefined
        }
      />
    </div>
  )
}

function ClientAvatar({ client }: { client: ClientItem }) {
  return (
    <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold">
      {client.avatarUrl ? (
        <img
          src={client.avatarUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        client.name.slice(0, 1).toUpperCase()
      )}
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Users
  label: string
  value: string
  tone: 'primary' | 'green' | 'blue'
}) {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-3 ${toneClasses[tone]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm text-slate-600">{label}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  )
}
